import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from 'react-router-dom'

// Cuánto tiempo se sigue intentando restaurar el scroll al volver. La grilla de
// la tienda puede tardar en tener el alto final (imágenes, catálogo en un F5).
const RESTORE_TIMEOUT_MS = 1500;
const STORAGE_KEY = "scroll-positions";

function leerPosiciones() {
    try {
        return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
    } catch {
        return {};
    }
}

function guardarPosiciones(posiciones) {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(posiciones));
    } catch {
        // sin storage (modo privado): la restauración sigue andando en memoria
    }
}

// Navegación nueva (PUSH/REPLACE) -> arriba de todo. Atrás/adelante (POP) ->
// la posición en la que estaba el usuario en esa entrada del historial.
//
// Antes subía arriba en TODO cambio de pathname, incluido el "atrás". En
// desktop no se notaba porque la grilla de 3 columnas entra casi entera en la
// pantalla; en mobile (2 columnas, paginador al fondo) el usuario volvía del
// detalle de un producto al principio de la grilla, sin ver el número de página,
// y lo leía como "me mandó a otra página".
const ScrollToTop = ({children}) => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const posiciones = useRef(leerPosiciones());
    const keyActual = useRef(location.key);
    const pathnameAnterior = useRef(null);
    const cancelarRestauracion = useRef(() => {});

    // El scroll lo manejamos nosotros: si no, el navegador restaura antes de
    // que React pinte la página y queda a mitad de camino.
    useEffect(() => {
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, []);

    // Se guarda la posición en cada scroll, asociada a la entrada del historial
    // vigente. NO se guarda al salir: para entonces el DOM ya es el de la página
    // nueva y el navegador pudo haber recortado el scroll.
    useEffect(() => {
        const onScroll = () => {
            posiciones.current[keyActual.current] = window.scrollY;
        };
        const persistir = () => guardarPosiciones(posiciones.current);
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("pagehide", persistir);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("pagehide", persistir);
        };
    }, []);

    useLayoutEffect(() => {
        keyActual.current = location.key;

        // Mismo pathname = cambió solo la query. La tienda reescribe ?page=/?cat=
        // con replace, y eso crea una entrada (key) nueva: hereda la posición
        // actual y no se toca el scroll (el de cambiar de página lo maneja la grilla).
        if (pathnameAnterior.current === location.pathname) {
            posiciones.current[location.key] = window.scrollY;
            return;
        }
        pathnameAnterior.current = location.pathname;
        guardarPosiciones(posiciones.current);
        cancelarRestauracion.current();

        const destino = navigationType === "POP" ? posiciones.current[location.key] : undefined;
        if (!destino) {
            document.documentElement.scrollTo(0, 0);
            return;
        }

        // La página puede no tener todavía el alto suficiente: se reintenta
        // cuadro a cuadro hasta llegar, o hasta que el usuario toque la pantalla.
        // La cancelación vive en un ref y NO en el cleanup del efecto: un replace
        // de la query en el medio (misma página) no tiene que cortarla.
        let frame;
        const limite = Date.now() + RESTORE_TIMEOUT_MS;
        const cancelar = () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("wheel", cancelar);
            window.removeEventListener("touchstart", cancelar);
        };
        const intentar = () => {
            window.scrollTo(0, destino);
            if (Math.abs(window.scrollY - destino) > 2 && Date.now() < limite) {
                frame = requestAnimationFrame(intentar);
            } else {
                cancelar();
            }
        };
        cancelarRestauracion.current = cancelar;
        window.addEventListener("wheel", cancelar, { passive: true });
        window.addEventListener("touchstart", cancelar, { passive: true });
        intentar();
    }, [location.key, location.pathname, navigationType]);

    useEffect(() => () => cancelarRestauracion.current(), []);

    return children
};

export default ScrollToTop;
